import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { MetricBarCard } from './MetricBarCard';
import { ConfidenceLevel } from '@features/campaigns/components/ConfidenceLevel';
import { buyerChartOptions } from './chart-options/buyer-chart-options';
import { salesChartOptions } from './chart-options/sales-chart-options';
import { purchaseChartsProps } from './chart-options/purchase-chart-props';
import { formatters } from '@utils/formattersV2';

const SHOW_METRIC_CARDS = false;

type PurchaseMetricsProps = {
  allReports: Parameters<typeof purchaseChartsProps>[0]['reports'];
};

export const PurchaseMetrics = ({ allReports }: PurchaseMetricsProps) => {
  const {
    incrementalSalesLift,
    price,
    frequency,
    penetration,
    buyerBreakdown,
  } = purchaseChartsProps({
    reports: allReports,
  });

  return (
    <Box>
      {SHOW_METRIC_CARDS && (
        <Grid container spacing={2} mb={2}>
          <Grid size={12}>
            <Card>
              <CardContent>
                <Box display="flex" width="100%">
                  <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    data-testid="incremental-sales-lift-metric"
                  >
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      {incrementalSalesLift.title}
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="h4" fontWeight="bold" mr={1}>
                        {formatters.percentage(incrementalSalesLift.lift, {
                          decimals: 1,
                        })}
                      </Typography>
                      <ConfidenceLevel
                        value={incrementalSalesLift.confidenceLevel}
                        lift={incrementalSalesLift.lift}
                      />
                    </Box>
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  <Box flex={1}>
                    <MetricBarCard
                      title={price.title}
                      lift={price.lift}
                      rows={price.rows}
                      valueFormatter={price.valueFormatter}
                      testId="avg-price-per-transaction-lift-metric"
                    />
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  <Box flex={1}>
                    <MetricBarCard
                      title={frequency.title}
                      lift={frequency.lift}
                      rows={frequency.rows}
                      valueFormatter={frequency.valueFormatter}
                      testId="avg-purchase-frequency-lift-metric"
                    />
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  <Box flex={1}>
                    <MetricBarCard
                      title={penetration.title}
                      lift={penetration.lift}
                      rows={penetration.rows}
                      valueFormatter={penetration.valueFormatter}
                      testId="penetration-metric"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card>
            <CardContent>
              <HighchartsReact
                highcharts={Highcharts}
                options={buyerChartOptions({
                  existingBuyerPercentage:
                    buyerBreakdown.existingBuyerPercentage,
                  newBuyerPercentage: buyerBreakdown.newBuyerPercentage,
                })}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card>
            <CardContent>
              <HighchartsReact
                highcharts={Highcharts}
                options={salesChartOptions({
                  existingBuyerSales: buyerBreakdown.existingBuyerSales,
                  newBuyerSales: buyerBreakdown.newBuyerSales,
                })}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
